import React from 'react'

const BehaviorPatterns = ({ mainData, selectedMember, orgMembers }) => {
  const styles = {
    wrapper: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '40px 20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    topSection: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '30px',
      gap: '20px',
    },
    leftSection: {
      flex: 1,
    },
    growthTitle: {
      fontSize: '64px',
      fontWeight: 'bold',
      color: '#2d8659',
      margin: 0,
      marginBottom: '10px',
      letterSpacing: '2px',
    },
    userName: {
      fontSize: '48px',
      fontWeight: '400',
      color: '#1a1a1a',
      margin: 0,
      borderBottom: '2px solid #1a1a1a',
      paddingBottom: '10px',
    },
    rightSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '15px',
    },
    logo: {
      backgroundColor: '#3a3a3a',
      padding: '30px 60px 30px 30px',
      borderRadius: '80px 0 0 80px',
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      position: 'relative',
    },
    logoIconWrapper: {
      width: '70px',
      height: '70px',
      backgroundColor: '#ffffff',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },

    logoText: {
      color: '#ffffff',
      fontSize: '32px',
      fontWeight: '400',
      letterSpacing: '1px',
    },
    header: {
      backgroundColor: '#d4e3df',
      padding: '20px',
      borderRadius: '12px',
      marginBottom: '40px',
    },
    title: {
      fontSize: '32px',
      fontWeight: 'bold',
      textAlign: 'center',
      margin: 0,
      letterSpacing: '1px',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '40px',
      marginBottom: '40px',
    },
    patternCard: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    },
    patternHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
    },
    circle: {
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      backgroundColor: '#2d8659',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    placeholderIcon: {
      width: '32px',
      height: '32px',
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      borderRadius: '4px',
    },
    patternName: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#2d8659',
      margin: 0,
    },
    description: {
      fontSize: '20px',
      color: '#1a1a1a',
      margin: 0,
      lineHeight: '1.4',
    },
  }

  const resultOne = mainData?.resultOne || {}
  const resultTwo = mainData?.resultTwo || {}
  const resultThree = mainData?.resultThree || {}
  const resultFour = mainData?.resultFour || {}
  const resultFive = mainData?.resultFive || {}
  const resultSix = mainData?.resultSix || {}

  const getUserName = () => {
    if (!selectedMember || !orgMembers || orgMembers.length === 0) {
      return 'User Name'
    }

    const member = orgMembers.find((m) => m.value === selectedMember)
    return member ? member.label : 'User Name'
  }

  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    return months[month - 1] || 'January'
  }

  return (
    <div style={styles.container}>
      <div style={styles.topSection}>
        <div style={styles.leftSection}>
          <h1 style={styles.growthTitle}>GROWTH PROFILE</h1>
          <h2 style={styles.userName}>{getUserName()}</h2>
        </div>

        <div style={styles.rightSection}>
          <div style={styles.logo}>
            <div style={styles.logoIconWrapper}>
              <img src="https://storage.googleapis.com/growdie/contents/manual-uploads/RGB-green_web_icon_300x300.png" alt="Growdie" style={{ width: '45px', height: '45px' }} />
            </div>
            <span style={styles.logoText}>growdie</span>
          </div>
        </div>
      </div>

      <div style={styles.header}>
        <h1 style={styles.title}>BEHAVIOR PATTERNS</h1>
      </div>

      {mainData?.totalItemsCompleted > 0 ? (
        <div style={styles.grid}>
          {/* Dynamic Pattern 1 - Builder */}
          <div style={styles.patternCard}>
            <div style={styles.patternHeader}>
              <div style={{ ...styles.circle, backgroundColor: resultOne.enumOneColor }}>
                <div style={styles.placeholderIcon}></div>
              </div>
              <h2 style={{ ...styles.patternName, color: resultOne.enumOneColor }}>{resultOne.enumOneResult || 'Builder'}</h2>
            </div>
            <p style={styles.description}>
              {resultOne.habitCompletionPercentage ? `${parseFloat(resultOne.habitCompletionPercentage).toFixed(1)}% habit completion` : '0% habit completion'}
            </p>
          </div>

          {/* Dynamic Pattern 2 - Pacer */}
          <div style={styles.patternCard}>
            <div style={styles.patternHeader}>
              <div style={{ ...styles.circle, backgroundColor: resultFour.enumFourColor }}>
                <div style={styles.placeholderIcon}></div>
              </div>
              <h2 style={{ ...styles.patternName, color: resultFour.enumFourColor }}>{resultFour.enumFourResult || 'Steady'}</h2>
            </div>
            <p style={styles.description}>
              {resultFour.combinedPercent
                ? ` ${resultFour.habitGoalDonePercent}% activites from habits/goals, ${parseFloat(resultFour.combinedPercent).toFixed(1)}% days logged`
                : '0% activites from habits/goals, 100% days logged'}
            </p>
          </div>

          {/* Static Pattern 3 - Steady */}
          <div style={styles.patternCard}>
            <div style={styles.patternHeader}>
              <div style={{ ...styles.circle, backgroundColor: resultTwo.enumTwoColor }}>
                <div style={styles.placeholderIcon}></div>
              </div>
              <h2 style={{ ...styles.patternName, color: resultTwo.enumTwoColor }}>{resultTwo.enumTwoResult || 'Steady'}</h2>
            </div>
            <p style={styles.description}>
              {resultTwo.activityCompletionPercentage
                ? `${parseFloat(resultTwo.activityCompletionPercentage).toFixed(1)}% completion rate on all activities`
                : '0% completion rate on all activities'}
            </p>
          </div>

          {/* Static Pattern 4 - Connector */}
          <div style={styles.patternCard}>
            <div style={styles.patternHeader}>
              <div style={{ ...styles.circle, backgroundColor: resultFive.enumFiveColor }}>
                <div style={styles.placeholderIcon}></div>
              </div>
              <h2 style={{ ...styles.patternName, color: resultFive.enumFiveColor }}>{resultFive.enumFiveResult || 'Connector'}</h2>
            </div>
            <p style={styles.description}>{resultFive?.pepPercentage}% People Development points</p>
          </div>

          {/* Static Pattern 5 - Finisher */}
          <div style={styles.patternCard}>
            <div style={styles.patternHeader}>
              <div style={{ ...styles.circle, backgroundColor: resultThree.enumThreeColor }}>
                <div style={styles.placeholderIcon}></div>
              </div>
              <h2 style={{ ...styles.patternName, color: resultThree.enumThreeColor }}>{resultThree.enumThreeResult || 'Finisher'}</h2>
            </div>

            <p style={styles.description}>
              {resultThree.challengeGoalCompletionPercentage
                ? `${parseFloat(resultThree.challengeGoalCompletionPercentage).toFixed(1)}% completion rate on challenges/goals`
                : '0% completion rate on challenges/goals'}
            </p>
          </div>

          {/* Static Pattern 6 - Integrator */}
          <div style={styles.patternCard}>
            <div style={styles.patternHeader}>
              <div style={{ ...styles.circle, backgroundColor: resultSix.enumSixColor }}>
                <div style={styles.placeholderIcon}></div>
              </div>
              <h2 style={{ ...styles.patternName, color: resultSix.enumSixColor }}>{resultSix.enumSixResult || 'Integrator'}</h2>
            </div>
            <p style={styles.description}>{resultSix.areasAbove8Percent} focus areas ≥8% of points</p>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', fontSize: '22px', marginBottom: '20px', color: '#555' }}>No completed items found to evaluate behavior patterns.</div>
      )}
    </div>
  )
}

export default BehaviorPatterns
